import { useEffect, useRef, useState } from "react";
import {
  FaFacebookF,
  FaInstagram,
  FaTiktok,
  FaYoutube,
  FaSnapchatGhost,
  FaGoogle,
} from "react-icons/fa";
import { BRAND_COLORS } from "./ServiceIcon";

const partners = [
  { Icon: FaFacebookF, name: "Facebook", colorKey: "facebook" },
  { Icon: FaInstagram, name: "Instagram", colorKey: "instagram" },
  { Icon: FaTiktok, name: "TikTok", colorKey: "tiktok" },
  { Icon: FaYoutube, name: "YouTube", colorKey: "youtube" },
  { Icon: FaSnapchatGhost, name: "Snapchat", colorKey: "snapchat" },
  { Icon: FaGoogle, name: "Google", colorKey: "google" },
];

const ICON_POOL = partners.map((p) => p.Icon);

const SlotLogo = ({ partner, index, trigger }) => {
  const startDelay = index * 140; // ms
  const slotDuration = 1100; // ms
  const cycleInterval = 70; // ms between random faces during slot
  const totalSteps = Math.floor(slotDuration / cycleInterval);

  const [step, setStep] = useState(0);
  const [settled, setSettled] = useState(false);
  const [started, setStarted] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!trigger || startedRef.current) return;
    startedRef.current = true;
    setStarted(true);

    let stepCounter = 0;
    let intervalId;
    const startTimer = setTimeout(() => {
      intervalId = setInterval(() => {
        stepCounter += 1;
        setStep(stepCounter);
        if (stepCounter >= totalSteps) {
          clearInterval(intervalId);
          setSettled(true);
        }
      }, cycleInterval);
    }, startDelay);

    return () => {
      clearTimeout(startTimer);
      if (intervalId) clearInterval(intervalId);
    };
  }, [trigger, startDelay, totalSteps]);

  const RandomIcon =
    !settled && step > 0
      ? ICON_POOL[(step + index) % ICON_POOL.length]
      : partner.Icon;

  const displayedIcon = settled ? partner.Icon : RandomIcon;
  const color = settled ? BRAND_COLORS[partner.colorKey] : "#a1a1aa";

  return (
    <div
      data-testid={`partner-logo-${partner.name.toLowerCase()}`}
      data-settled={settled}
      data-started={started}
      className="rm-slot-logo flex items-center gap-3"
      style={{ "--brand": BRAND_COLORS[partner.colorKey] }}
    >
      <span
        key={settled ? "settled" : `step-${step}`}
        className={
          !started
            ? ""
            : settled
            ? "rm-slot-logo-icon-settled"
            : "rm-slot-logo-icon-spinning"
        }
        style={{ color, display: "inline-flex" }}
      >
        {(() => {
          const I = displayedIcon;
          return <I className="h-7 w-7" />;
        })()}
      </span>
      <span
        className="font-display text-lg font-semibold tracking-tight transition-colors duration-300"
        style={{ color: settled ? "#f8f8f8" : started ? "#52525b" : "#a1a1aa" }}
      >
        {partner.name}
      </span>
    </div>
  );
};

export const PartnerLogos = () => {
  const sectionRef = useRef(null);
  const [trigger, setTrigger] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      // Fallback — fire immediately if API unavailable
      setTrigger(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setTrigger(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.35, rootMargin: "0px 0px -10% 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      data-testid="partner-logos-section"
      className="relative border-y border-zinc-800/80 bg-[#0a0a0c]/60 py-12 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 mb-8 flex items-center justify-between gap-4">
        <div className="rm-overline">Partnered Platforms</div>
        <div className="hidden md:block text-xs font-mono text-zinc-500">
          Certified / Managing spend across
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-8 items-center justify-items-start sm:justify-items-center">
          {partners.map((p, i) => (
            <SlotLogo key={p.name} partner={p} index={i} trigger={trigger} />
          ))}
        </div>
      </div>
    </section>
  );
};
